<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import HotelBookingsAPI from 'dashboard/api/hotelBookings';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  booking: { type: Object, default: null },
  rooms: { type: Array, default: () => [] },
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['saved', 'cancel']);
const { t } = useI18n();

const isSubmitting = ref(false);
const roomId = ref(props.booking?.hotel_room_id || '');
const checkIn = ref(props.booking?.check_in || '');
const checkOut = ref(props.booking?.check_out || '');
const adults = ref(props.booking?.adults || 1);
const children = ref(props.booking?.children || 0);
const children2 = ref(props.booking?.children2 || 0);
const isHoliday = ref(props.booking?.is_holiday || false);
const notes = ref(props.booking?.notes || '');
const totalPrice = ref(props.booking?.total_price || 0);

const selectedRoom = computed(() => props.rooms.find(r => r.id === Number(roomId.value)));

const initialRoomLoaded = ref(false);
watch(selectedRoom, room => {
  if (!room) return;
  if (props.booking && !initialRoomLoaded.value) {
    initialRoomLoaded.value = true;
    return;
  }
  adults.value = room.max_guests || 1;
});

watch(() => checkIn.value, newVal => {
  if (newVal) {
    const d = new Date(newVal);
    d.setDate(d.getDate() + 1);
    checkOut.value = d.toISOString().split('T')[0];
  }
});

const nights = computed(() => {
  if (!checkIn.value || !checkOut.value) return 0;
  const diff = Math.round((new Date(checkOut.value) - new Date(checkIn.value)) / 86400000);
  return diff > 0 ? diff : 0;
});

const getPrice = (room, n, prefix) => {
  switch (n) {
    case 1: return Number(room[`${prefix}1_night`]) || 0;
    case 2: return Number(room[`${prefix}2_nights`]) || 0;
    case 3: return Number(room[`${prefix}3_nights`]) || 0;
    default: {
      const base = Number(room[`${prefix}3_nights`]) || 0;
      return base > 0 ? Math.round((base / 3) * n * 100) / 100 : 0;
    }
  }
};

const adultPrice = computed(() => {
  if (!selectedRoom.value || nights.value <= 0) return 0;
  const prefix = isHoliday.value ? 'holiday_price_' : 'price_';
  return getPrice(selectedRoom.value, nights.value, prefix);
});

const child1Price = computed(() => {
  if (!selectedRoom.value || nights.value <= 0 || children.value <= 0) return 0;
  const prefix = isHoliday.value ? 'holiday_child1_price_' : 'child1_price_';
  return getPrice(selectedRoom.value, nights.value, prefix) * children.value;
});

const child2Price = computed(() => {
  if (!selectedRoom.value || nights.value <= 0 || children2.value <= 0) return 0;
  const prefix = isHoliday.value ? 'holiday_child2_price_' : 'child2_price_';
  return getPrice(selectedRoom.value, nights.value, prefix) * children2.value;
});

const calculatedPrice = computed(() => adultPrice.value + child1Price.value + child2Price.value);

watch(calculatedPrice, newVal => { totalPrice.value = newVal; });

const formatCurrency = value => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const isFormValid = computed(() => roomId.value && checkIn.value && checkOut.value && nights.value > 0 && adults.value > 0);

const handleSubmit = async () => {
  if (!isFormValid.value) return;
  isSubmitting.value = true;
  const payload = {
    hotel_booking: {
      hotel_room_id: Number(roomId.value),
      contact_id: props.contactId,
      conversation_id: props.conversationId,
      check_in: checkIn.value,
      check_out: checkOut.value,
      guests: Number(adults.value) + Number(children.value) + Number(children2.value),
      adults: Number(adults.value),
      children: Number(children.value),
      children2: Number(children2.value),
      is_holiday: isHoliday.value,
      total_price: Number(totalPrice.value),
      notes: notes.value,
      status: 'budget',
    },
  };
  try {
    if (props.booking?.id) {
      await HotelBookingsAPI.update(props.booking.id, payload);
      useAlert(t('HOTEL_BOOKING.API.UPDATE_SUCCESS'));
    } else {
      await HotelBookingsAPI.create(payload);
      useAlert(t('HOTEL_BOOKING.API.CREATE_SUCCESS'));
    }
    emit('saved');
  } catch (error) {
    /* eslint-disable no-console */
    console.error('[HotelBooking] error:', error?.response?.status, error?.response?.data);
    /* eslint-enable no-console */
    const msg = error?.response?.data?.message || error?.response?.data?.error;
    useAlert(msg || t('HOTEL_BOOKING.API.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const ic = 'w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none';
</script>

<template>
  <form class="flex flex-col gap-4 p-4" @submit.prevent="handleSubmit">
    <!-- Room -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_BOOKING.FORM.ROOM.LABEL') }}</label>
      <select v-model="roomId" :class="ic">
        <option value="" disabled>{{ $t('HOTEL_BOOKING.FORM.ROOM.PLACEHOLDER') }}</option>
        <option v-for="room in rooms" :key="room.id" :value="room.id">{{ room.name }} ({{ room.max_guests }} hósp.)</option>
      </select>
    </div>

    <!-- Room info -->
    <div v-if="selectedRoom" class="bg-n-slate-2 rounded-lg p-3 text-xs text-n-slate-11">
      <div v-if="selectedRoom.amenities" class="mb-1"><span class="font-medium text-n-slate-12">Incluso:</span> {{ selectedRoom.amenities }}</div>
      <div class="flex gap-4 flex-wrap">
        <span><span class="font-medium">Máx:</span> {{ selectedRoom.max_guests }}</span>
        <span v-if="selectedRoom.free_age_limit > 0"><span class="font-medium">Grátis até:</span> {{ selectedRoom.free_age_limit }} anos</span>
      </div>
      <div class="flex gap-4 mt-1 flex-wrap">
        <span><span class="font-medium">Faixa 1:</span> {{ selectedRoom.child1_min_age }}-{{ selectedRoom.child1_max_age }} anos</span>
        <span><span class="font-medium">Faixa 2:</span> {{ selectedRoom.child2_min_age }}-{{ selectedRoom.child2_max_age }} anos</span>
      </div>
    </div>

    <!-- Holiday toggle -->
    <div class="flex items-center gap-3 p-3 rounded-lg" :class="isHoliday ? 'bg-n-amber-2 border border-n-amber-7' : 'bg-n-slate-2 border border-n-weak'">
      <input id="is-holiday" v-model="isHoliday" type="checkbox" class="rounded border-n-weak text-n-amber-9 focus:ring-n-amber-9" />
      <label for="is-holiday" class="text-sm font-medium" :class="isHoliday ? 'text-n-amber-11' : 'text-n-slate-11'">Tarifa de Feriado</label>
      <span v-if="isHoliday" class="text-xs text-n-amber-10 ml-auto">Valores de feriado</span>
    </div>

    <!-- Dates -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_BOOKING.FORM.CHECK_IN.LABEL') }}</label>
        <input v-model="checkIn" type="date" :class="ic" />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_BOOKING.FORM.CHECK_OUT.LABEL') }}</label>
        <input v-model="checkOut" type="date" :class="ic" />
      </div>
    </div>
    <div v-if="nights > 0" class="text-xs text-n-brand-11 font-medium">{{ nights }} {{ nights === 1 ? 'diária' : 'diárias' }}</div>

    <!-- Adults -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_BOOKING.FORM.ADULTS.LABEL') }}</label>
      <input v-model.number="adults" type="number" min="1" :class="ic" />
    </div>

    <!-- Children (2 ranges) -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          Crianças
          <span v-if="selectedRoom" class="text-n-slate-10 font-normal">({{ selectedRoom.child1_min_age }}-{{ selectedRoom.child1_max_age }} anos)</span>
        </label>
        <input v-model.number="children" type="number" min="0" :class="ic" />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          Crianças
          <span v-if="selectedRoom" class="text-n-slate-10 font-normal">({{ selectedRoom.child2_min_age }}-{{ selectedRoom.child2_max_age }} anos)</span>
        </label>
        <input v-model.number="children2" type="number" min="0" :class="ic" />
      </div>
    </div>

    <!-- Price breakdown -->
    <div v-if="calculatedPrice > 0" class="bg-n-slate-2 rounded-lg p-3 text-xs">
      <div v-if="isHoliday" class="text-n-amber-11 font-medium mb-2">Tarifa de Feriado</div>
      <div class="flex justify-between text-n-slate-11">
        <span>{{ adults }} adulto(s) x {{ nights }} diária(s)</span>
        <span>{{ formatCurrency(adultPrice) }}</span>
      </div>
      <div v-if="children > 0" class="flex justify-between text-n-slate-11 mt-1">
        <span>{{ children }} criança(s) {{ selectedRoom?.child1_min_age }}-{{ selectedRoom?.child1_max_age }}a x {{ nights }}d</span>
        <span>{{ formatCurrency(child1Price) }}</span>
      </div>
      <div v-if="children2 > 0" class="flex justify-between text-n-slate-11 mt-1">
        <span>{{ children2 }} criança(s) {{ selectedRoom?.child2_min_age }}-{{ selectedRoom?.child2_max_age }}a x {{ nights }}d</span>
        <span>{{ formatCurrency(child2Price) }}</span>
      </div>
      <div class="flex justify-between text-n-slate-12 font-semibold mt-2 pt-2 border-t border-n-weak">
        <span>Total</span>
        <span>{{ formatCurrency(calculatedPrice) }}</span>
      </div>
    </div>

    <!-- Total (editable) -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_BOOKING.FORM.TOTAL_PRICE.LABEL') }}</label>
      <input v-model.number="totalPrice" type="number" step="0.01" min="0" :class="ic" />
    </div>

    <!-- Notes -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">{{ $t('HOTEL_BOOKING.FORM.NOTES.LABEL') }}</label>
      <textarea v-model="notes" rows="2" :class="ic" :placeholder="$t('HOTEL_BOOKING.FORM.NOTES.PLACEHOLDER')" />
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-end gap-2 pt-2">
      <Button :label="$t('HOTEL_BOOKING.FORM.CANCEL')" slate sm @click="emit('cancel')" />
      <Button :label="$t('HOTEL_BOOKING.FORM.SUBMIT')" sm :disabled="!isFormValid || isSubmitting" :is-loading="isSubmitting" @click="handleSubmit" />
    </div>
  </form>
</template>
